import { useCallback, useMemo } from 'react';
import FlagIcon from '@heroicons/react/outline/FlagIcon';
import { hasActionPermission, hasInputPermission, } from '@tribeplatform/gql-client';
import { useNetworkRoles, useUpdateMember, } from '@tribeplatform/react-sdk/hooks';
import { confirm } from '@tribeplatform/react-ui-kit/Dialog';
import { toast } from '@tribeplatform/react-ui-kit/Toast';
import { useI18n } from '../../i18n';
export const useFlagMemberActionItem = member => {
    var _a, _b, _c, _d;
    const { $t } = useI18n();
    const { mutateAsync: updateMember } = useUpdateMember();
    const roles = useNetworkRoles();
    const categoryText = $t({
        id: 'Generics.Member',
        defaultMessage: '{MEMBER_CC}',
    });
    const updateMemberInputPermissions = (_d = (_c = hasActionPermission((_b = (_a = member === null || member === void 0 ? void 0 : member.authMemberProps) === null || _a === void 0 ? void 0 : _a.permissions) !== null && _b !== void 0 ? _b : [], 'updateMember')) === null || _c === void 0 ? void 0 : _c.actionPermission) === null || _d === void 0 ? void 0 : _d.inputPermissions;
    const { authorized: canFlagMember } = hasInputPermission(updateMemberInputPermissions, 'input.flagged');
    const onFlag = useCallback(async (member) => {
        if (!(member === null || member === void 0 ? void 0 : member.id)) {
            return;
        }
        const confirmed = await confirm({
            title: $t({
                defaultMessage: 'Flagging {MEMBER}',
                id: 'MemberAction.Flag.Confirmation',
            }),
            description: $t({
                defaultMessage: "Flagging a {MEMBER} will send all their posts and replies for moderators' approval first.",
                id: 'MemberAction.Flag.Confirmation.Description',
            }),
            proceedLabel: $t({
                defaultMessage: 'Flag',
                id: 'Generics.Flag.Verb',
            }),
            cancelLabel: $t({
                defaultMessage: 'Cancel',
                id: 'Generics.Cancel',
            }),
        });
        if (!confirmed) {
            return;
        }
        try {
            await updateMember({
                id: member.id,
                input: {
                    flagged: true,
                },
            });
            if (!member.flagged) {
                toast({
                    title: $t({
                        defaultMessage: '{MEMBER_CC} successfully flagged',
                        id: 'MemberAction.Flagged',
                    }),
                    status: 'success',
                });
            }
            if (member.flagged) {
                toast({
                    title: $t({
                        defaultMessage: '{MEMBER_CC} successfully unflagged',
                        id: 'MemberAction.Unflagged',
                    }),
                    status: 'success',
                });
            }
        }
        catch {
            toast({
                title: $t({
                    defaultMessage: 'Something went wrong',
                    id: 'Generics.SomethingWentWrong',
                }),
                description: $t({
                    defaultMessage: 'Cannot flag this {MEMBER}.',
                    id: 'MemberAction.FailedToFlag.Description',
                }),
                status: 'error',
            });
        }
    }, [$t, updateMember]);
    const onUnflag = useCallback(async (member) => {
        try {
            await updateMember({
                id: member.id,
                input: {
                    flagged: false,
                },
            });
            toast({
                title: $t({
                    defaultMessage: '{MEMBER_CC} successfully unflagged',
                    id: 'MemberAction.Unflagged',
                }),
                status: 'success',
            });
        }
        catch {
            toast({
                title: $t({
                    defaultMessage: 'Something went wrong',
                    id: 'Generics.SomethingWentWrong',
                }),
                description: $t({
                    defaultMessage: 'Cannot unflag this {MEMBER}.',
                    id: 'MemberAction.FailedToUnflag.Description',
                }),
                status: 'error',
            });
        }
    }, [$t, updateMember]);
    const flagMemberActionItem = useMemo(() => {
        var _a, _b, _c, _d;
        if (((_a = member === null || member === void 0 ? void 0 : member.role) === null || _a === void 0 ? void 0 : _a.type) !== ((_b = roles === null || roles === void 0 ? void 0 : roles.admin) === null || _b === void 0 ? void 0 : _b.type) &&
            ((_c = member === null || member === void 0 ? void 0 : member.role) === null || _c === void 0 ? void 0 : _c.type) !== ((_d = roles === null || roles === void 0 ? void 0 : roles.moderator) === null || _d === void 0 ? void 0 : _d.type) &&
            !(member === null || member === void 0 ? void 0 : member.flagged) &&
            canFlagMember) {
            return {
                action: () => onFlag(member),
                icon: FlagIcon,
                name: $t({ defaultMessage: 'Flag {MEMBER}', id: 'Member.Flag' }),
                category: categoryText,
            };
        }
        if ((member === null || member === void 0 ? void 0 : member.flagged) && canFlagMember) {
            return {
                action: () => onUnflag(member),
                icon: FlagIcon,
                name: $t({ defaultMessage: 'Unflag {MEMBER}', id: 'Member.Unflag' }),
                category: categoryText,
            };
        }
        return null;
    }, [$t, canFlagMember, categoryText, member, onFlag, onUnflag, roles]);
    return flagMemberActionItem;
};
//# sourceMappingURL=useFlagMemberActionItem.js.map